/**
 * Public marketing landing page (FR-MARKETING-01/02/03).
 *
 * The page is a football field you scroll down. The hero is a night game seen
 * from behind the end zone, the goal post framing the headline and a kick
 * sailing through the uprights on load. Below it the background turns into a
 * top-down field: each section is a snap on one drive (yard lines and field
 * numbers pass by, a broadcast-style down & distance bug and a drive chart
 * follow the scroll), pricing sits in the red zone and the final call to
 * action is the touchdown in the end zone.
 *
 * Pre-auth and API-free. All art is inline SVG + CSS (see field.tsx and
 * mockups.tsx); motion respects `prefers-reduced-motion`.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { ROUTES, TRIAL } from '../../lib/constants';
import {
  DRIVE,
  DRIVE_FINISH,
  HERO,
  PROGRAM_SPECS,
  type DriveStop,
} from '../../lib/marketing-content';

import { DriveTracker, GoalPost, HeroStadium, useRevealOnScroll, YardLine } from './field';
import { MarketingChrome, TierGrid } from './marketing-chrome';
import {
  FanPhoneMock,
  GameSummaryMock,
  HighlightMock,
  HudlMock,
  PlayEntryMock,
  StatTrackerMock,
} from './mockups';

const MOCKUPS: Record<string, () => JSX.Element> = {
  'play-entry': PlayEntryMock,
  'game-summary': GameSummaryMock,
  highlight: HighlightMock,
  stats: StatTrackerMock,
  hudl: HudlMock,
  fans: FanPhoneMock,
};

const DRIVE_MARKERS = [
  ...DRIVE.map(({ id, down, spot, yard, lineToGain }) => ({ id, down, spot, yard, lineToGain })),
  { id: 'program', ...DRIVE_FINISH.program },
  { id: 'pricing', ...DRIVE_FINISH.pricing },
  { id: 'touchdown', ...DRIVE_FINISH.touchdown },
];

/** Broadcast-style down & distance tag that heads every section. */
function DownTag({ down, spot }: { down: string; spot: string }): JSX.Element {
  return (
    <p className="down-tag">
      <span className="down-tag__down">{down}</span>
      <span className="down-tag__spot">{spot}</span>
    </p>
  );
}

/** The fan door: fans following a team need a clear way in (F1/F6). */
function FanDoor({ className = '' }: { className?: string }): JSX.Element {
  return (
    <div className={`fan-door ${className}`}>
      <p>
        <strong>Here to watch a team?</strong> You don’t need a team code or an invite.
      </p>
      <div className="btn-row">
        <Link className="btn btn--chalk" to={ROUTES.fanSignup}>
          Follow your team
        </Link>
        <Link className="btn btn--ghost" to={ROUTES.fanLogin}>
          Fan sign in
        </Link>
      </div>
    </div>
  );
}

function Snap({
  stop,
  index,
  children,
}: {
  stop: DriveStop;
  index: number;
  children: ReactNode;
}): JSX.Element {
  return (
    <section
      className={`snap${index % 2 === 1 ? ' snap--flip' : ''}`}
      id={stop.id}
      aria-labelledby={`${stop.id}-title`}
    >
      <div className="snap__copy">
        <DownTag down={stop.down} spot={stop.spot} />
        <p className="snap__kicker">{stop.kicker}</p>
        <h2 id={`${stop.id}-title`}>{stop.title}</h2>
        <p className="snap__body">{stop.body}</p>
        <ul className="snap__points">
          {stop.points.map((point) => (
            <li key={point}>{point}</li>
          ))}
        </ul>
      </div>
      <div className="snap__art">{children}</div>
    </section>
  );
}

export function LandingPage(): JSX.Element {
  useRevealOnScroll();
  return (
    <MarketingChrome variant="field">
      <section className="hero" aria-labelledby="hero-title">
        <HeroStadium />
        <div className="hero__inner">
          <p className="hero__kicker">{HERO.kicker}</p>
          <GoalPost>
            <h1 id="hero-title" className="hero__title">
              <span>{HERO.headlineTop}</span>{' '}
              <span className="hero__title-accent">{HERO.headlineBottom}</span>
            </h1>
          </GoalPost>
          <p className="hero__sub">{HERO.subheadline}</p>
          <div className="btn-row hero__ctas">
            <Link className="btn btn--primary btn--lg" to={HERO.primaryCtaHref}>
              {HERO.primaryCta}
            </Link>
            <a className="btn btn--chalk btn--lg" href="#play-entry">
              {HERO.secondaryCta} <span aria-hidden="true">↓</span>
            </a>
          </div>
          <p className="hero__trial">{HERO.trialCallout}</p>
          <FanDoor className="fan-door--hero" />
        </div>
      </section>

      <div className="field">
        <div className="field__turf" aria-hidden="true" />
        <DriveTracker markers={DRIVE_MARKERS} />
        <div className="field__plays">
          {DRIVE.map((stop, index) => {
            const Mock = MOCKUPS[stop.id];
            return (
              <div className="field__stop" key={stop.id}>
                <YardLine yard={stop.yard} />
                <Snap stop={stop} index={index}>
                  <Mock />
                </Snap>
              </div>
            );
          })}

          <div className="field__stop">
            <YardLine yard={DRIVE_FINISH.program.yard} />
            <section className="program" id="program" aria-labelledby="program-title">
              <DownTag down={DRIVE_FINISH.program.down} spot={DRIVE_FINISH.program.spot} />
              <h2 id="program-title">The rest of the program, in the same place</h2>
              <dl className="program__specs">
                {PROGRAM_SPECS.map((spec) => (
                  <div key={spec.term}>
                    <dt>{spec.term}</dt>
                    <dd>{spec.detail}</dd>
                  </div>
                ))}
              </dl>
            </section>
          </div>

          <div className="field__stop field__stop--redzone">
            <YardLine yard={80} />
            <section className="redzone" id="pricing" aria-labelledby="pricing-title">
              <DownTag down={DRIVE_FINISH.pricing.down} spot="Red zone" />
              <h2 id="pricing-title">One subscription per program</h2>
              <p className="redzone__sub">
                Priced per program, not per coach. Start on the full {TRIAL.planDisplayName} plan
                for {TRIAL.days} days, then pick the plan that fits your staff.
              </p>
              <TierGrid />
              <p className="redzone__more">
                {HERO.trialCallout} <Link to={ROUTES.pricing}>Compare plans in detail</Link>
              </p>
            </section>
          </div>
        </div>

        <section className="endzone" id="touchdown" aria-labelledby="endzone-title">
          <YardLine yard={100} />
          <div className="endzone__paint" aria-hidden="true">
            <span>Touchdown</span>
          </div>
          <div className="endzone__inner">
            <h2 id="endzone-title">Your next game, charted live</h2>
            <p>
              Set up your team, invite your stat crew and log your next scrimmage. The{' '}
              {TRIAL.planDisplayName} trial needs no card.
            </p>
            <div className="btn-row">
              <Link className="btn btn--primary btn--lg" to={ROUTES.signup}>
                {HERO.primaryCta}
              </Link>
              <Link className="btn btn--chalk btn--lg" to={ROUTES.login}>
                Team sign in
              </Link>
            </div>
            <FanDoor />
          </div>
        </section>
      </div>
    </MarketingChrome>
  );
}
